'use client';

import React, { useState } from 'react';
import { validateEntryLocally, type JournalEntryLine } from '../utils/accounting.utils';
import { Card, CardContent } from '@/components/ui/card';

interface JournalEntryFormProps {
  onSubmit: (data: any) => Promise<void>;
  isLoading?: boolean;
  initialData?: any;
}

export const JournalEntryForm: React.FC<JournalEntryFormProps> = ({ 
  onSubmit, 
  isLoading = false,
  initialData 
}) => {
  const [description, setDescription] = useState(initialData?.description || '');
  const [lines, setLines] = useState<JournalEntryLine[]>(initialData?.lines || []);

  const updateLine = (index: number, field: string, value: any) => {
    const newLines = [...lines];
    newLines[index] = { ...newLines[index], [field]: value };
    setLines(newLines);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const validation = validateEntryLocally(lines);
      if (!validation.valid) {
        console.error('[v0] Validation error:', validation.errors);
        return;
      }
      await onSubmit({ description, lines });
    } catch (error) {
      console.error('[v0] Submit error:', error);
    }
  };

  return (
    <Card className="bg-muted/30">
      <CardContent className="p-6">
    <form id="journal-entry-form" onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium mb-1">Descripció</label>
        <input
          type="text"
          value={description}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDescription(e.target.value)}
          className="w-full px-3 py-2 border rounded"
          placeholder="Descripció de l'assentament"
        />
      </div>

      <div className="space-y-2">
        <h3 className="font-semibold">Línies</h3>
        {lines.map((line, index) => (
          <div key={index} className="grid grid-cols-5 gap-2 p-2 border rounded">
            <input
              type="text"
              value={line.accountCode || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateLine(index, 'accountCode', e.target.value)}
              placeholder="Codi"
              className="px-2 py-1 border rounded text-sm"
            />
            <input
              type="text"
              value={line.description || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateLine(index, 'description', e.target.value)}
              placeholder="Descripció"
              className="px-2 py-1 border rounded text-sm"
            />
            <input
              type="number"
              value={line.debit || 0}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateLine(index, 'debit', e.target.value ? parseFloat(e.target.value) : 0)}
              placeholder="Deure"
              className="px-2 py-1 border rounded text-sm"
            />
            <input
              type="number"
              value={line.credit || 0}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateLine(index, 'credit', e.target.value ? parseFloat(e.target.value) : 0)}
              placeholder="Haver"
              className="px-2 py-1 border rounded text-sm"
            />
            <button
              type="button"
              onClick={() => setLines(lines.filter((_, i) => i !== index))}
              className="px-2 py-1 bg-red-500 text-white rounded text-sm"
            >
              Eliminar
            </button>
          </div>
        ))}
      </div>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setLines([...lines, { accountCode: '', description: '', debit: 0, credit: 0 }])}
          className="px-4 py-2 bg-gray-600 text-white rounded"
        >
          + Afegir Línia
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="px-4 py-2 bg-green-500 text-white rounded disabled:opacity-50"
        >
          {isLoading ? 'Desant...' : 'Desar'}
        </button>
      </div>
    </form>
      </CardContent>
    </Card>
  );
};
